import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Grid, Paper,
  TextField, Button, IconButton, Tooltip, FormControl, InputLabel, Select,
  MenuItem, Switch, FormControlLabel, Divider, Typography, CircularProgress
} from '@mui/material';
import { Close, Visibility, Save, Add } from '@mui/icons-material';
import QuestionEditor, { QUESTION_TYPES } from './QuestionEditor';
import FormPreview from './FormPreview';

export const FORM_TYPES = [
  { value: 'SURVEY', label: 'Survey', color: 'primary' },
  { value: 'FEEDBACK', label: 'Feedback', color: 'success' },
  { value: 'APPLICATION', label: 'Application', color: 'warning' },
  { value: 'REGISTRATION', label: 'Registration', color: 'info' },
  { value: 'QUIZ', label: 'Quiz', color: 'secondary' },
  { value: 'ORDER', label: 'Order Form', color: 'error' },
  { value: 'OTHER', label: 'Other', color: 'default' },
];

export const THEME_COLORS = [
  '#673ab7', '#3f51b5', '#2196f3', '#00bcd4', '#009688',
  '#4caf50', '#ff9800', '#ff5722', '#e91e63', '#795548'
];

const FormBuilder = ({ 
  open, 
  onClose, 
  formData, 
  setFormData, 
  onSave, 
  loading,
  aiLoading,
  onImproveQuestion 
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const addQuestion = (type = 'SHORT_TEXT') => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: 'Untitled Question',
      description: '',
      required: false,
      options: ['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(type) 
        ? ['Option 1', 'Option 2'] 
        : [],
      settings: { scaleMin: 1, scaleMax: 5 }
    };
    setFormData(prev => ({ ...prev, fields: [...prev.fields, newField] }));
  };

  const updateQuestion = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f)
    }));
  };

  const deleteQuestion = (index) => {
    setFormData(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
  };

  const duplicateQuestion = (index) => {
    const field = { ...formData.fields[index], id: `field_${Date.now()}` };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields.slice(0, index + 1), field, ...prev.fields.slice(index + 1)]
    }));
  };

  const updateSettings = (key, value) => {
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth 
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: formData.themeColor, 
            color: '#fff', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <TextField
            variant="standard"
            value={formData.title || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Form Title"
            sx={{ 
              flex: 1,
              mr: 2,
              '& input': { color: '#fff', fontSize: '1.4rem', fontWeight: 500 },
              '& .MuiInput-underline:before': { borderColor: 'rgba(255,255,255,0.5)' },
              '& .MuiInput-underline:hover:before': { borderColor: 'rgba(255,255,255,0.8)' },
              '& .MuiInput-underline:after': { borderColor: '#fff' }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Preview">
              <IconButton sx={{ color: '#fff' }} onClick={() => setPreviewOpen(true)}>
                <Visibility />
              </IconButton>
            </Tooltip>
            <IconButton sx={{ color: '#fff' }} onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f5f5f5', p: 3 }}>
          <Grid container spacing={3}>
            {/* Main Editor Area */}
            <Grid item xs={12} md={8}>
              {/* Form Header Card */}
              <Paper sx={{ p: 3, mb: 2, borderTop: `10px solid ${formData.themeColor}` }}>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Form description (optional)"
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  maxRows={3}
                />
              </Paper>

              {/* Questions */}
              {formData.fields && formData.fields.length > 0 ? (
                formData.fields.map((field, index) => (
                  <QuestionEditor
                    key={field.id || `field_${index}`}
                    field={field}
                    index={index}
                    themeColor={formData.themeColor}
                    onUpdate={updateQuestion}
                    onDelete={deleteQuestion}
                    onDuplicate={duplicateQuestion}
                    onImproveWithAI={onImproveQuestion}
                    aiLoading={aiLoading}
                  />
                ))
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
                  <Typography color="textSecondary">
                    No questions yet. Add questions using the buttons below.
                  </Typography>
                </Paper>
              )}

              {/* Add Question Buttons */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Add Question
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {QUESTION_TYPES.slice(0, 7).map(type => (
                    <Button
                      key={type.value}
                      variant="outlined"
                      size="small"
                      startIcon={type.icon}
                      onClick={() => addQuestion(type.value)}
                      sx={{ textTransform: 'none' }}
                    >
                      {type.label}
                    </Button>
                  ))}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => addQuestion('SHORT_TEXT')}
                  >
                    Other
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Settings Sidebar */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Form Settings
                </Typography>

                {/* Form Type */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Form Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Form Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    {FORM_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Theme Color */}
                <Typography variant="caption" color="textSecondary">
                  Theme Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2, mt: 0.5 }}>
                  {THEME_COLORS.map(color => (
                    <Box
                      key={color}
                      onClick={() => setFormData(prev => ({ ...prev, themeColor: color }))}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: color,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: formData.themeColor === color ? '3px solid #000' : '2px solid transparent',
                        '&:hover': { transform: 'scale(1.1)' },
                        transition: 'all 0.2s'
                      }}
                    />
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Settings Toggles */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings?.collectEmail || false}
                      onChange={(e) => updateSettings('collectEmail', e.target.checked)}
                    />
                  }
                  label="Collect email addresses"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings?.showProgressBar || false}
                      onChange={(e) => updateSettings('showProgressBar', e.target.checked)}
                    />
                  }
                  label="Show progress bar"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings?.isActive !== false}
                      onChange={(e) => updateSettings('isActive', e.target.checked)}
                    />
                  }
                  label="Form is active"
                />

                <Divider sx={{ my: 2 }} />

                {/* Confirmation Message */}
                <TextField
                  fullWidth
                  size="small"
                  label="Confirmation Message"
                  value={formData.settings?.confirmationMessage || ''}
                  onChange={(e) => updateSettings('confirmationMessage', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Thank you for your response!"
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={onSave}
            disabled={loading}
            sx={{ bgcolor: formData.themeColor }}
          >
            {loading ? 'Saving...' : 'Save Form'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Form Preview</Typography>
          <IconButton onClick={() => setPreviewOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f0ebf8', p: 0 }}>
          <FormPreview formData={formData} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FormBuilder;
